import {
  Check,
  FileText,
  Mail,
  Phone,
  Receipt,
  RefreshCw,
  Search,
  ShoppingCart,
  Trash2,
  User,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { businessInfoAPI, itemsAPI, salesAPI } from "../../api";
import { useCurrency } from "../../contexts/CurrencyContext";
import { useDiscountApproval } from "../../contexts/DiscountApprovalContext";
import { useAppStore } from "../../stores/useAppStore";
import dayjs from "../../utils/dayjs";
import { acceptsTypedText } from "../../utils/keyboard";
import { printSaleReceipt } from "../../utils/pdf";
import {
  CheckoutInfoModal,
  CurrencyInput,
  ItemListComponent,
  PercentageInput,
} from "../ui";

const POSInterface = () => {
  const { addNotification } = useAppStore();
  const { formatPrice } = useCurrency();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchingById, setIsSearchingById] = useState(false);
  const [idQueue, setIdQueue] = useState([]);
  const [isProcessingIdQueue, setIsProcessingIdQueue] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemQuantities, setItemQuantities] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { discountApprovalFlowLauncher } = useDiscountApproval();

  // Sale form state
  const [saleForm, setSaleForm] = useState({
    SaleDate: new Date().toISOString().slice(0, 10), // Default to today's date
    CustomerPhone: "",
    CustomerName: "",
    Remarks: "",
  });

  // Totals
  const [computedTotals, setComputedTotals] = useState({
    subtotal: 0,
    netAmount: 0,
    productDiscount: 0,
    productDiscountPercentage: 0,
    additionalDiscount: 0,
    additionalDiscountPercentage: 0,
  });

  // Ref for immediate access to current totals
  const computedTotalsRef = useRef(computedTotals);

  // Keep ref in sync with state
  useEffect(() => {
    computedTotalsRef.current = computedTotals;
  }, [computedTotals]);

  // Checkout dialog state
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cashReceived, setCashReceived] = useState(0);
  const [bankReceived, setBankReceived] = useState(0);

  // Duplicate item modal state
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateItem, setDuplicateItem] = useState(null);
  // Max stock modal state
  const [maxStockModalOpen, setMaxStockModalOpen] = useState(false);
  const [maxStockItem, setMaxStockItem] = useState(null);
  // Zero price modal state
  const [zeroPriceModalOpen, setZeroPriceModalOpen] = useState(false);
  const [zeroPriceItems, setZeroPriceItems] = useState([]);

  // Success dialog state
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [saleData, setSaleData] = useState(null);
  const hasTriggeredPrintRef = useRef(false);

  // Cart review ("Send for Review") state. The button only appears when an admin
  // has configured it — cashiers can't read business information themselves, so
  // the backend answers this one question for them.
  const [cartReviewEnabled, setCartReviewEnabled] = useState(false);
  const [isSendingCartReview, setIsSendingCartReview] = useState(false);
  const [cartReviewSent, setCartReviewSent] = useState(false);
  // Mirrors isSendingCartReview for the guards that can't see React state: the
  // keyboard listener holds a stale closure, and state updates are batched, so
  // a fast Ctrl+Enter right after F8 would still read `false`.
  const isSendingCartReviewRef = useRef(false);

  // Submission state to prevent double sales
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Turns on after a Make Sale attempt fails validation, so the required
  // customer name/phone fields get a red outline until they're valid.
  const [validationAttempted, setValidationAttempted] = useState(false);

  // Ref for consolidated search input
  const searchInputRef = useRef(null);
  // Ref for additional discount percentage input
  const additionalDiscountPercentageRef = useRef(null);
  // Ref for net amount input
  const netAmountInputRef = useRef(null);

  const handleClearSaleForm = () => {
    setSaleForm({
      SaleDate: new Date().toISOString().slice(0, 10), // Reset to today's date
      CustomerPhone: "",
      CustomerName: "",
      Remarks: "",
    });
    setComputedTotals((prev) => ({
      ...prev,
      additionalDiscount: 0,
      productDiscountPercentage: 0,
      additionalDiscountPercentage: 0,
    }));
    // Clear the red required-field highlight for the next sale
    setValidationAttempted(false);
  };

  // Auto-generate and print when success dialog opens
  useEffect(() => {
    if (successDialogOpen && saleData && !hasTriggeredPrintRef.current) {
      hasTriggeredPrintRef.current = true;
      handleRenderPDF();
    }
  }, [successDialogOpen, saleData]);

  // Ask once on mount whether this deploy offers the cart review button.
  useEffect(() => {
    let cancelled = false;
    businessInfoAPI
      .getCartReviewConfig()
      .then((response) => {
        if (!cancelled) setCartReviewEnabled(!!response.data?.enabled);
      })
      .catch(() => {
        // Older backend, or the call failed — just don't offer the button.
        if (!cancelled) setCartReviewEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A display-only snapshot of the cart. Shared by the discount approval OTP
  // email and the cart review email so the two can never drift apart.
  const buildSaleSummary = useCallback(() => {
    const currentTotals = computedTotalsRef.current;
    return {
      items: selectedItems.map((item) => {
        const quantity = itemQuantities[item.ID] || 1;
        const unitPrice = Number(item.Price || 0);
        const salePrice = Number(item.SalePrice || 0);
        return {
          name: item.Name || '',
          quantity,
          // unit_price is the retail/list price; unit_sale_price is what the
          // customer actually pays right now. The email shows both so whoever
          // prices the special discount can see what's already been given
          // before deciding how much more to add.
          unit_price: unitPrice,
          unit_sale_price: salePrice || unitPrice,
          line_total: quantity * unitPrice,
          item_discount: quantity * Math.max(unitPrice - salePrice, 0),
        };
      }),
      subtotal: Number(currentTotals.subtotal || 0),
      item_discount_total: Number(currentTotals.productDiscount || 0),
      requested_discount_amount: Number(currentTotals.additionalDiscount || 0),
      net_total:
        Number(currentTotals.subtotal || 0) -
        Number(currentTotals.productDiscount || 0) -
        Number(currentTotals.additionalDiscount || 0),
      customer_name: saleForm.CustomerName || '',
      customer_phone: saleForm.CustomerPhone || '',
      exchange_difference: null,
    };
  }, [selectedItems, itemQuantities, saleForm.CustomerName, saleForm.CustomerPhone]);

  // Customer name and phone gate both Make Sale and Send for Review. Shared so
  // the two can't drift on the phone format or the wording of the error.
  const validateCustomerFields = () => {
    const customerName = (saleForm.CustomerName || "").trim();
    const customerPhone = (saleForm.CustomerPhone || "").trim();
    if (!customerName) {
      setValidationAttempted(true);
      addNotification({
        type: "error",
        message: "Customer name is required",
      });
      return false;
    }
    if (!/^03\d{9}$/.test(customerPhone)) {
      setValidationAttempted(true);
      addNotification({
        type: "error",
        message: "Customer phone is required (11 digits, e.g. 03001234567)",
      });
      return false;
    }
    // Both required fields are valid — clear any prior red highlight
    setValidationAttempted(false);
    return true;
  };

  // Email the open cart to whoever prices special discounts. Requires the same
  // customer fields as Make Sale: the reviewer is pricing a discount for a
  // named person, and the sale that follows needs them anyway, so collecting
  // them up front costs nothing.
  const handleSendCartReview = async () => {
    if (
      !cartReviewEnabled ||
      selectedItems.length === 0 ||
      isSendingCartReviewRef.current
    )
      return;

    if (!validateCustomerFields()) return;

    try {
      isSendingCartReviewRef.current = true;
      setIsSendingCartReview(true);
      const response = await businessInfoAPI.sendCartReviewEmail({
        sale_summary: buildSaleSummary(),
      });

      // Briefly swap the label to a confirmation so the cashier can see it
      // landed without having to read the toast.
      setCartReviewSent(true);
      setTimeout(() => setCartReviewSent(false), 4000);

      addNotification({
        type: "success",
        message: response.data?.message || "Cart sent for review",
      });
    } catch (error) {
      console.error("Error sending cart for review:", error);
      addNotification({
        type: "error",
        message:
          error.response?.data?.detail || "Failed to send cart for review",
      });
    } finally {
      isSendingCartReviewRef.current = false;
      setIsSendingCartReview(false);
    }
  };

  // The keyboard effect below is registered with a narrow dependency list, so a
  // handler captured directly in it would go stale — F8 would email the cart as
  // it looked when the listener was last attached (old quantities, old
  // discount). Reading through a ref that every render refreshes keeps the
  // shortcut and the button sending exactly the same thing.
  const sendCartReviewRef = useRef(handleSendCartReview);
  useEffect(() => {
    sendCartReviewRef.current = handleSendCartReview;
  });

  const additionaDiscountReasonRef = useRef("");
  const handleOpenCheckout = async () => {
    // Don't let a sale start while a cart review is still in flight. The
    // overlay covers the button, but Ctrl+Enter and F9 go through a document
    // listener that no amount of CSS can block, so the real gate is here.
    if (isSendingCartReviewRef.current) return;

    // Customer name and phone are mandatory for every sale
    if (!validateCustomerFields()) return;

    // Reset cash and bank received immediately when opening checkout
    setCashReceived(0);
    setBankReceived(0);

    // Pre-check for items with zero price (use SalePrice if set, otherwise fall back to Price)
    const itemsWithZeroPrice = selectedItems.filter((item) => {
      const effectivePrice = !item.SalePrice
        ? Number(item.SalePrice)
        : Number(item.Price || 0);
      return effectivePrice === 0;
    });
    if (itemsWithZeroPrice.length > 0) {
      setZeroPriceItems(itemsWithZeroPrice);
      setZeroPriceModalOpen(true);
      return;
    }

    // Use ref for immediate access to current values
    const currentTotals = computedTotalsRef.current;

    try {
      // Build a display-only summary of the cart for the approval email
      const saleSummary = buildSaleSummary();

      // Use promise-based discount approval flow
      const result = await discountApprovalFlowLauncher(
        currentTotals.additionalDiscountPercentage,
        saleSummary,
      );

      if (result) {
        additionaDiscountReasonRef.current = result.reason;
        setCheckoutOpen(true);
      } else {
        // Handle rejection (maybe show a message or reset discount)
        addNotification({
          type: "error",
          message: "Discount rejected",
        });
      }
    } catch (error) {
      console.error("Error in discount approval flow:", error);
      // Handle error (maybe show error message)
    }
  };

  const handleCloseCheckout = () => {
    setCheckoutOpen(false);
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    setSaleData(null);
    hasTriggeredPrintRef.current = false;

    // Refresh the page to reset the interface
    window.location.reload();
  };

  // Generate PDF receipt and prompt for printing
  const handleRenderPDF = async () => {
    if (!saleData) {
      console.error("No sale data available for PDF generation");
      return;
    }
    try {
      const adjustedSaleData = JSON.parse(JSON.stringify(saleData));

      // Build a minimal sale shape expected by printSaleReceipt
      const details = adjustedSaleData.items
        .map((item) => {
          return {
            ...item,
            item: { Name: item.Name, Unit: item.Unit },
            Quantity: adjustedSaleData.itemQuantities?.[item.ID] || 1,
          };
        })
        .map((item) => ({
          ...item,
          Price: Number(item.Price || 0),
          Discount: Number((item.Price || 0) - (item.SalePrice || 0)),
        }));

      const saleForPrint = {
        SaleDate: saleData.SaleDate || new Date().toISOString(),
        SaleNo: saleData.SaleNo || saleData.ID,
        BankReceived: saleData.totals?.bankReceived ?? bankReceived,
        CashReceived: saleData.totals?.cashReceived ?? cashReceived,
        CashReturned: saleData.totals?.cashReturned ?? cashReturned,
        Discount: saleData?.Discount || 0,
        details,
      };

      await printSaleReceipt(saleForPrint);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF receipt. Please try again.");
    }
  };

  // Use the EXACT product discount (sum of per-item Price−SalePrice), not a
  // value re-derived from the rounded productDiscountPercentage. The % is a
  // read-only, 2-decimal-rounded display of productDiscount/subtotal; feeding
  // it back through subtotal × % reintroduced a sub-rupee error that skewed the
  // "Discount on Net" base, leaving a stray ±paisa net on fully-discounted
  // sales (e.g. Rs0.90 / −Rs0.96). productDiscount already matches what the
  // backend stores per line, so this reconciles the saved sale exactly.
  const calculatedProductDiscount = Number(computedTotals?.productDiscount || 0);
  const netAmount =
    Math.round(
      ((computedTotals?.subtotal || 0) -
        calculatedProductDiscount -
        (computedTotals?.additionalDiscount || 0)) *
        100,
    ) / 100;
  // Round net amount to the nearest whole number (standard rounding)
  const roundedNetAmount = Math.round(netAmount);
  const cashReturned = Math.max(
    (Number(cashReceived) || 0) +
      (Number(bankReceived) || 0) -
      roundedNetAmount,
    0,
  );
  const totalReceived =
    (Number(cashReceived) || 0) + (Number(bankReceived) || 0);
  const isPaymentValid =
    totalReceived >= roundedNetAmount && roundedNetAmount > 0;

  const generateSaleNo = () => {
    const year = dayjs().format("YY");
    const month = dayjs().format("MM");
    const day = dayjs().format("DD");

    const hour = dayjs().format("HH");
    const minute = dayjs().format("mm");
    const seconds = dayjs().format("ss");

    return year + month + day + hour + minute + seconds;
  };

  const handleConfirmCheckout = async () => {
    // Prevent double submission
    if (isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      SaleNo: generateSaleNo(),
      CustomerPhone: saleForm.CustomerPhone || undefined,
      CustomerName: saleForm.CustomerName || undefined,
      Remarks: saleForm.Remarks || undefined,
      SaleRemarks: saleForm.Remarks || undefined,
      productDiscount: Number(calculatedProductDiscount) || 0,
      additionalDiscount: Number(computedTotals?.additionalDiscount) || 0,
      CashReceived: Number(cashReceived) || 0,
      BankReceived: Number(bankReceived) || 0,
      CashReturned: Number(cashReturned) || 0,
      syncToWebsite: false,
      items: selectedItems.map((item) => {
        return {
          ID: item.ID,
          quantity: itemQuantities[item.ID] || 1,
        };
      }),
    };

    if (additionaDiscountReasonRef.current) {
      payload.SaleRemarks += `\nAdditional Discount Reason: ${additionaDiscountReasonRef.current}`;
    }

    try {
      const res = await salesAPI.create(payload);
      setCheckoutOpen(false);

      // Store sale data for success dialog
      setSaleData({
        ...res.data,
        SaleNo: payload.SaleNo,
        items: selectedItems,
        itemQuantities: itemQuantities, // Store quantities before clearing cart
        Discount: computedTotals.additionalDiscount,
        totals: {
          subtotal: computedTotals.subtotal,
          productDiscount: calculatedProductDiscount,
          netAmount: roundedNetAmount,
          cashReceived: cashReceived,
          bankReceived: bankReceived,
          cashReturned: cashReturned,
        },
      });

      // Show success dialog
      setSuccessDialogOpen(true);

      // Clear the cart and form
      setSelectedItems([]);
      setItemQuantities({});
      handleClearSaleForm();
    } catch (err) {
      console.error("Failed to create sale:", err);

      // Handle duplicate sale error (409 Conflict)
      if (
        err.response?.status === 409 &&
        err.response?.data?.detail?.existing_sale_id
      ) {
        const errorData = err.response.data.detail;
        addNotification({
          type: "error",
          title: "Duplicate Sale",
          message: `${errorData.message} (Sale ID: ${errorData.existing_sale_id})`,
          duration: 8000,
        });
      }
      // Handle stock validation errors
      else if (
        err.response?.status === 400 &&
        err.response?.data?.detail?.insufficient_stock_items
      ) {
        const errorData = err.response.data.detail;
        const insufficientItems = errorData.insufficient_stock_items;

        // Create detailed error message for user
        let errorMessage = "Insufficient stock for the following items:\n\n";
        insufficientItems.forEach((item, index) => {
          errorMessage += `${index + 1}. ${item.item_name} (ID: ${item.item_id})\n`;
          errorMessage += `   Requested: ${item.requested_quantity}\n`;
          errorMessage += `   Available: ${item.current_stock}\n`;
          errorMessage += `   Shortage: ${item.shortage}\n\n`;
        });

        errorMessage +=
          "Please reduce quantities or remove items with insufficient stock.";

        addNotification({
          type: "error",
          title: "Insufficient Stock",
          message: errorMessage,
          duration: 10000, // Show for 10 seconds since it's a detailed message
        });
      } else {
        // Handle other errors
        const errorMessage =
          err.response?.data?.detail?.message ||
          err.response?.data?.detail ||
          "Failed to create sale. Please try again.";

        addNotification({
          type: "error",
          title: "Sale Failed",
          message: errorMessage,
        });
      }
    } finally {
      setIsSubmitting(false);
    }

    additionaDiscountReasonRef.current = "";
  };

  // Select an item from search/dropdown (respects Stock cap by product_id)
  const handleItemSelect = useCallback(
    (item) => {
      if (!item) return;

      if (!item.Stock || item.Stock == 0) {
        return;
      }

      item.Price = Number(item.Price || 0);
      item.WebsiteStock = Number(item.WebsiteStock || 0);
      item.SalePrice = Number(item.SalePrice || 0);
      item.Stock = Number(item.Stock || 0);

      const availableRaw = parseFloat(item.Stock);
      const available = Number.isFinite(availableRaw)
        ? Math.max(0, Math.floor(availableRaw))
        : 0;

      // Check if item already exists in selectedItems
      const existingItemIndex = selectedItems.findIndex(
        (si) => si?.ID == item.ID,
      );

      if (existingItemIndex !== -1) {
        // Item already exists, increment its quantity and move to top
        const currentQuantity = itemQuantities[item.ID] || 1;
        if (currentQuantity < available) {
          setItemQuantities((prev) => ({
            ...prev,
            [item.ID]: currentQuantity + 1,
          }));

          // Move existing item to top of the list
          setSelectedItems((prevItems) => {
            const updatedItems = [...prevItems];
            const [movedItem] = updatedItems.splice(existingItemIndex, 1);
            return [movedItem, ...updatedItems];
          });
        } else {
          // Show max stock modal if trying to exceed available stock
          setMaxStockItem(item);
          setMaxStockModalOpen(true);
        }
      } else {
        // Item doesn't exist, add it to the top of the list
        setSelectedItems((prevItems) => [item, ...prevItems]);
        setItemQuantities((prev) => ({
          ...prev,
          [item.ID]: 1,
        }));
      }

      setSearchTerm("");
      setSearchResults([]);
    },
    [selectedItems, itemQuantities],
  );

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await itemsAPI.search(searchTerm);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error("Error searching items:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchTerm]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, handleSearch]);

  const handleSaleFormChange = (field, value) => {
    setSaleForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Enter handler for consolidated search input.
  // If typeahead has results, add the first one (manual flow).
  // Otherwise enqueue the raw value for the scanner-fast path (debounce hasn't fired yet).
  const handleSearchEnter = useCallback(() => {
    const trimmed = String(searchTerm || "").trim();
    if (!trimmed) return;
    if (searchResults.length > 0) {
      handleItemSelect(searchResults[0]);
      setSearchTerm("");
      setSearchResults([]);
      return;
    }
    setIdQueue((prev) => [...prev, trimmed]);
    setSearchTerm("");
  }, [searchTerm, searchResults, handleItemSelect]);

  // Process the ID queue sequentially
  useEffect(() => {
    const processNext = async () => {
      if (isProcessingIdQueue || idQueue.length === 0) return;
      setIsProcessingIdQueue(true);
      setIsSearchingById(true);
      const nextId = idQueue[0];
      try {
        const response = await itemsAPI.getById(nextId);
        const item = response?.data?.[0];
        if (item) {
          handleItemSelect(item);
        } else {
          // Scanned/typed code matched no item — tell the cashier instead of
          // failing silently, so a wrong or truncated barcode is obvious.
          addNotification({
            type: "warning",
            message: `No item found for "${nextId}"`,
          });
        }
      } catch (error) {
        console.error("Error fetching item by ID (queue):", error);
        addNotification({
          type: "error",
          message: `Couldn't look up "${nextId}". Please try again.`,
        });
      } finally {
        // dequeue
        setIdQueue((prev) => prev.slice(1));
        setIsSearchingById(false);
        setIsProcessingIdQueue(false);
      }
    };
    processNext();
  }, [idQueue, isProcessingIdQueue, handleItemSelect, addNotification]);

  // Helper functions for item status
  const getStockStatus = (item) => {
    const currentStock = parseFloat(item.Stock) || 0;
    const minQuantity = parseFloat(item.MinimunQuantity) || 0;

    if (currentStock <= 0)
      return {
        status: "out",
        color: "bg-red-100 text-red-800",
        text: "Out of Stock",
      };
    if (currentStock <= minQuantity)
      return {
        status: "low",
        color: "bg-yellow-100 text-yellow-800",
        text: "Low Stock",
      };
    return {
      status: "good",
      color: "bg-green-100 text-green-800",
      text: "In Stock",
    };
  };

  const getExpiryStatus = (item) => {
    if (!item.TimeToZero) return null;
    const expiryDate = new Date(item.TimeToZero);
    const today = new Date();
    const daysUntilExpiry = Math.ceil(
      (expiryDate - today) / (1000 * 60 * 60 * 24),
    );

    if (daysUntilExpiry <= 0)
      return {
        status: "expired",
        color: "bg-red-100 text-red-800",
        text: "Expired",
      };
    if (daysUntilExpiry <= 7)
      return {
        status: "expiring",
        color: "bg-yellow-100 text-yellow-800",
        text: `${daysUntilExpiry} days left`,
      };
    return null;
  };

  const handleQuantityChange = (itemId, quantity) => {
    const item = selectedItems.find((i) => i.ID === itemId);
    const availableRaw = item ? parseFloat(item.Stock) : 0;
    const available = Number.isFinite(availableRaw)
      ? Math.max(0, Math.floor(availableRaw))
      : 0;
    const clamped = Math.max(1, Math.min(quantity, available || 1));
    setItemQuantities((prev) => ({
      ...prev,
      [itemId]: clamped,
    }));
  };

  const handleQuantityIncrement = (itemId) => {
    const currentQuantity = itemQuantities[itemId] || 1;
    const item = selectedItems.find((i) => i.ID === itemId);
    const availableRaw = item ? parseFloat(item.Stock) : 0;
    const available = Number.isFinite(availableRaw)
      ? Math.max(0, Math.floor(availableRaw))
      : 0;

    if (currentQuantity < available) {
      setItemQuantities((prev) => ({
        ...prev,
        [itemId]: currentQuantity + 1,
      }));
    }
  };

  const handleQuantityDecrement = (itemId) => {
    const currentQuantity = itemQuantities[itemId] || 1;
    if (currentQuantity > 1) {
      setItemQuantities((prev) => ({
        ...prev,
        [itemId]: currentQuantity - 1,
      }));
    }
  };

  const handleRemoveSelectedItem = (itemId) => {
    setSelectedItems((prevItems) =>
      prevItems.filter((item) => item.ID !== itemId),
    );
    setItemQuantities((prev) => {
      const newQuantities = { ...prev };
      delete newQuantities[itemId];
      return newQuantities;
    });
  };

  const handleClearAllSelectedItems = () => {
    setSelectedItems([]);
    setItemQuantities({});
  };

  const handleDiscountChange = (discount) => {
    const newAmount = Number(discount) || 0;
    const baseAfterProductDiscount =
      computedTotals.subtotal - calculatedProductDiscount;
    const newPercentage =
      baseAfterProductDiscount > 0
        ? Math.round((newAmount / baseAfterProductDiscount) * 100 * 100) / 100
        : 0;
    setComputedTotals((prev) => ({
      ...prev,
      additionalDiscount: newAmount,
      additionalDiscountPercentage: newPercentage,
    }));
  };

  const handleAdditionalDiscountPercentageChange = (percentage) => {
    const newPercentage = Math.round((Number(percentage) || 0) * 100) / 100;
    const baseAfterProductDiscount =
      computedTotals.subtotal - calculatedProductDiscount;
    const calculatedAmount =
      Math.round(((baseAfterProductDiscount * newPercentage) / 100) * 100) /
      100;

    setComputedTotals((prev) => {
      const newState = {
        ...prev,
        additionalDiscountPercentage: newPercentage,
        additionalDiscount: calculatedAmount,
      };
      return newState;
    });
  };

  const handleNetAmountChange = (netAmount) => {
    const newNetAmount = Number(netAmount) || 0;
    const subtotalAfterProductDiscount =
      computedTotals.subtotal - calculatedProductDiscount;
    const calculatedAdditionalDiscount = Math.max(
      0,
      subtotalAfterProductDiscount - newNetAmount,
    );
    const calculatedAdditionalDiscountPercentage =
      subtotalAfterProductDiscount > 0
        ? Math.round(
            (calculatedAdditionalDiscount / subtotalAfterProductDiscount) *
              100 *
              100,
          ) / 100
        : 0;

    setComputedTotals((prev) => ({
      ...prev,
      additionalDiscount: calculatedAdditionalDiscount,
      additionalDiscountPercentage: calculatedAdditionalDiscountPercentage,
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal =
      Math.round(
        selectedItems.reduce((total, item) => {
          const quantity = itemQuantities[item.ID] || 1;
          return total + quantity * parseFloat(item.Price || 0);
        }, 0) * 100,
      ) / 100;

    // Calculate product discounts (from individual items)
    const productDiscount =
      Math.round(
        selectedItems.reduce((total, item) => {
          const quantity = itemQuantities[item.ID] || 1;
          const itemDiscount = parseFloat(item.Price - item.SalePrice || 0);
          return total + quantity * itemDiscount;
        }, 0) * 100,
      ) / 100;

    // Calculate product discount percentage
    const productDiscountPercentage =
      subtotal > 0
        ? Math.round((productDiscount / subtotal) * 100 * 100) / 100
        : 0;

    setComputedTotals((prev) => {
      // If a percentage is set, recompute additional discount amount against (subtotal - productDiscount)
      const baseAfterProductDiscount = subtotal - productDiscount;
      const newAdditionalDiscount =
        (prev.additionalDiscountPercentage || 0) > 0
          ? Math.round(
              ((baseAfterProductDiscount * prev.additionalDiscountPercentage) /
                100) *
                100,
            ) / 100
          : prev.additionalDiscount || 0;

      return {
        subtotal,
        productDiscount,
        productDiscountPercentage: productDiscountPercentage,
        additionalDiscount: newAdditionalDiscount,
        additionalDiscountPercentage: prev.additionalDiscountPercentage || 0,
        netAmount: subtotal,
      };
    });
  };

  useEffect(() => {
    calculateTotals();
  }, [selectedItems, itemQuantities]);

  // Focus Item ID input on component mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Keyboard shortcut handler for Make Sale, Confirm, Success Modal, Max Stock Modal, and ESC key
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Handle ESC key to close any open modal
      if (event.key === "Escape") {
        event.preventDefault();

        if (zeroPriceModalOpen) {
          setZeroPriceModalOpen(false);
          setZeroPriceItems([]);
          // Focus barcode input after closing modal
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
              searchInputRef.current.select();
            }
          }, 100);
        } else if (maxStockModalOpen) {
          setMaxStockModalOpen(false);
          // Focus barcode input after closing modal
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
              searchInputRef.current.select();
            }
          }, 100);
        } else if (successDialogOpen) {
          handleCloseSuccessDialog();
        } else if (checkoutOpen) {
          handleCloseCheckout();
        }
        return;
      }

      // If zero price modal is open, handle Enter to close it
      if (zeroPriceModalOpen && event.key === "Enter" && !event.ctrlKey) {
        event.preventDefault();
        setZeroPriceModalOpen(false);
        setZeroPriceItems([]);
        // Focus barcode input after closing modal
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
          }
        }, 100);
        return;
      }

      // If max stock modal is open, handle Enter to close it
      if (maxStockModalOpen && event.key === "Enter" && !event.ctrlKey) {
        event.preventDefault();
        setMaxStockModalOpen(false);
        // Focus barcode input after closing modal
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
          }
        }, 100);
        return;
      }

      // If success modal is open, handle Enter to close it
      if (successDialogOpen && event.key === "Enter" && !event.ctrlKey) {
        event.preventDefault();
        handleCloseSuccessDialog();
        return;
      }

      // If checkout modal is open, let the CheckoutInfoModal handle its own keyboard shortcuts
      if (checkoutOpen) {
        return;
      }

      // Ctrl+Enter or F9 to trigger Make Sale (only when checkout is not open)
      if ((event.ctrlKey && event.key === "Enter") || event.key === "F9") {
        event.preventDefault();
        if (selectedItems.length > 0 && !checkoutOpen) {
          handleOpenCheckout();
        }
      }

      // F8 to email the cart for a special discount decision. Sits next to F9
      // (Make Sale) because it is the step that usually comes right before it.
      // The handler itself re-checks that the feature is on and the cart isn't
      // empty or mid-send.
      if (event.key === "F8") {
        event.preventDefault();
        if (!checkoutOpen) {
          sendCartReviewRef.current?.();
        }
      }

      // Shift+D and Shift+N are also how a capital D or N is typed, so they
      // only jump when the cursor isn't in a box that takes words (the item
      // search, customer name, remarks, the discount reason).
      const shiftJumpAllowed =
        event.shiftKey && !acceptsTypedText(event.target);

      // Shift+D to focus additional discount percentage input
      if (shiftJumpAllowed && event.key === "D") {
        event.preventDefault();
        if (additionalDiscountPercentageRef.current) {
          additionalDiscountPercentageRef.current.focus();
          additionalDiscountPercentageRef.current.select();
        }
      }

      // Shift+N to focus net amount input
      if (shiftJumpAllowed && event.key === "N") {
        event.preventDefault();
        if (netAmountInputRef.current) {
          netAmountInputRef.current.focus();
          netAmountInputRef.current.select();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    selectedItems.length,
    checkoutOpen,
    isPaymentValid,
    successDialogOpen,
    maxStockModalOpen,
    zeroPriceModalOpen,
  ]);

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] gap-6 p-6 overflow-visible">
      {/* Consolidated Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 flex-shrink-0 relative z-20">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by name, brand, or scan barcode..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearchEnter();
              }
            }}
            className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {(isSearching || isSearchingById) && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}
          {/* Typeahead Dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-2 max-h-80 overflow-auto border border-gray-200 rounded-lg bg-white shadow-lg z-[9999]">
              {searchResults.map((item) => {
                const stockStatus = getStockStatus(item);
                const expiryStatus = getExpiryStatus(item);
                return (
                  <div
                    key={item.ID}
                    onClick={() => handleItemSelect(item)}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {item.Name}
                        </h4>
                        {item.NameUrdu && (
                          <p className="text-xs text-gray-500 mt-1" dir="rtl">
                            {item.NameUrdu}
                          </p>
                        )}
                        <div className="flex gap-1 mt-1 flex-wrap">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}
                          >
                            {stockStatus.text}
                          </span>
                          {expiryStatus && (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${expiryStatus.color}`}
                            >
                              {expiryStatus.text}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          <span>ID: {item.ID}</span>
                          {item.Barcode && (
                            <span className="ml-2">
                              Barcode: {item.Barcode}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-blue-600">
                          {formatPrice(item.SalePrice)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Stock: {item.Stock}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* Scan queue progress */}
        {(idQueue.length > 0 || isProcessingIdQueue) && (
          <div className="mt-2 text-xs text-gray-600 flex items-center gap-2">
            <div className="h-3 w-3 rounded-full border-b-2 border-blue-600 animate-spin"></div>
            <span>Processing scan queue{isProcessingIdQueue ? "..." : ""}</span>
            {idQueue.length > 0 && (
              <span className="px-2 py-0.5 bg-gray-100 rounded-full border text-gray-700">
                {idQueue.length} pending
              </span>
            )}
          </div>
        )}
      </div>

      {/* Three columns row */}
      <div className="flex flex-1 min-h-0 gap-6 overflow-visible">
        {/* Column 1: Sale Information */}
        <div className="w-1/3 flex flex-col">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Sale Information
                </h2>
                <button
                  onClick={handleClearSaleForm}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Phone <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="tel"
                      placeholder="03XXXXXXXXX"
                      maxLength={11}
                      value={saleForm.CustomerPhone}
                      onChange={(e) =>
                        handleSaleFormChange("CustomerPhone", e.target.value)
                      }
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent ${
                        validationAttempted &&
                        !/^03\d{9}$/.test((saleForm.CustomerPhone || "").trim())
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:ring-blue-500"
                      }`}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    11 digits, e.g. 03001234567
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Customer full name"
                      value={saleForm.CustomerName}
                      onChange={(e) =>
                        handleSaleFormChange("CustomerName", e.target.value)
                      }
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent ${
                        validationAttempted && !(saleForm.CustomerName || "").trim()
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:ring-blue-500"
                      }`}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Required</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sale Remarks
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <textarea
                      placeholder="Add any additional notes for this sale"
                      rows={3}
                      maxLength={2000}
                      value={saleForm.Remarks}
                      onChange={(e) =>
                        handleSaleFormChange("Remarks", e.target.value)
                      }
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Visible on sale record
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Column 2: Selected Items List */}
        <div className="w-1/3 flex flex-col">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  {(() => {
                    const totalQty = selectedItems.reduce(
                      (sum, item) => sum + (itemQuantities[item.ID] || 1),
                      0,
                    );
                    return <span>Selected Items ({totalQty})</span>;
                  })()}
                </h3>
                {selectedItems.length > 0 && (
                  <button
                    onClick={handleClearAllSelectedItems}
                    className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <Trash2 className="h-4 w-4" />
                    Clear All
                  </button>
                )}
              </div>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              <ItemListComponent
                items={selectedItems}
                itemQuantities={itemQuantities}
                onQuantityIncrement={handleQuantityIncrement}
                onQuantityDecrement={handleQuantityDecrement}
                onRemoveItem={handleRemoveSelectedItem}
                emptyMessage="No Items Selected"
                emptyDescription="Search and select items from the bar above to add them to your cart."
                compact
                highlightZeroPrice
              />
            </div>
          </div>
        </div>

        {/* Column 3: Order Summary */}
        <div className="w-1/3 flex flex-col">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Order Summary
              </h3>
            </div>

            <div className="p-6 flex-1 flex flex-col">
              <div className="space-y-4 flex-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">
                    {formatPrice(computedTotals.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Product Discount:</span>
                  <span className="font-medium text-green-600">
                    -{formatPrice(computedTotals.productDiscount || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Product Discount %:</span>
                  <div className="flex items-center gap-2">
                    <PercentageInput
                      value={computedTotals.productDiscountPercentage || 0}
                      readOnly={true}
                      className="input-sm w-20 px-2 border border-gray-300 rounded-md bg-gray-50"
                      placeholder="0"
                      min={0}
                      max={100}
                      decimalPlaces={2}
                    />
                    <span className="text-xs text-gray-500">%</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Discount on Net %:</span>
                  <div className="flex items-center gap-2">
                    <PercentageInput
                      ref={additionalDiscountPercentageRef}
                      value={computedTotals.additionalDiscountPercentage || 0}
                      onChange={handleAdditionalDiscountPercentageChange}
                      className="input-sm w-20 px-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      min={0}
                      max={100}
                      decimalPlaces={2}
                    />
                    <span className="text-xs text-gray-500">%</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Discount on Net:</span>
                  <div className="flex items-center gap-2">
                    <CurrencyInput
                      // Pass a number, not a toFixed(2) string: CurrencyInput mirrors
                      // `value` into the box on every change, so "5" came back as
                      // "5.00" and the next digit was rejected as a 3rd decimal.
                      // Rounding only hides float noise from the Net Total path.
                      value={
                        Math.round(
                          (Number(computedTotals.additionalDiscount) || 0) * 100,
                        ) / 100
                      }
                      onChange={handleDiscountChange}
                      className="input-sm w-20 px-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      min={0}
                    />
                    <span className="text-xs text-gray-500">RS</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Discount %:</span>
                  <span className="text-sm font-medium text-blue-600">
                    {(computedTotals.subtotal > 0
                      ? ((calculatedProductDiscount +
                          (computedTotals.additionalDiscount || 0)) /
                          computedTotals.subtotal) *
                        100
                      : 0
                    ).toFixed(2)}
                    %
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">
                      Net Total:
                    </span>
                    <div className="flex items-center gap-2">
                      <CurrencyInput
                        ref={netAmountInputRef}
                        value={roundedNetAmount}
                        onChange={handleNetAmountChange}
                        className="input-lg w-24 px-2 font-semibold text-blue-600 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                        min={0}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex-shrink-0 space-y-3">
                {cartReviewEnabled && (
                  <button
                    onClick={handleSendCartReview}
                    disabled={selectedItems.length === 0 || isSendingCartReview}
                    className="w-full bg-white text-blue-700 border-2 border-blue-600 py-3 px-4 rounded-xl font-semibold hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
                    title="Email this cart for a special discount decision (F8)"
                  >
                    {isSendingCartReview ? (
                      <>
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : cartReviewSent ? (
                      <>
                        <Check className="h-5 w-5" />
                        Sent for Review
                      </>
                    ) : (
                      <>
                        <Mail className="h-5 w-5" />
                        Send for Review
                        <span className="text-sm opacity-75 ml-2">(F8)</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={handleOpenCheckout}
                  disabled={selectedItems.length === 0 || isSendingCartReview}
                  className="w-full bg-blue-600 text-white py-4 px-4 rounded-xl font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 text-lg"
                  title="Make Sale (Ctrl+Enter or F9)"
                >
                  <ShoppingCart className="h-6 w-6" />
                  Make Sale
                  <span className="text-sm opacity-75 ml-2">(Ctrl+Enter)</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blocking loader while a cart review is being emailed. Sending goes out
          over SMTP and can take a couple of seconds; without this the cashier
          can start the sale in that window and the reviewer ends up pricing a
          discount for an order that is already closed. */}
      {isSendingCartReview && (
        <div
          className="fixed inset-0 z-[100] bg-gray-900/50 backdrop-blur-sm flex items-center justify-center"
          role="alertdialog"
          aria-busy="true"
          aria-label="Sending cart for review"
        >
          <div className="bg-white rounded-2xl shadow-xl px-8 py-7 flex flex-col items-center gap-3 max-w-sm mx-4">
            <RefreshCw className="h-10 w-10 text-blue-600 animate-spin" />
            <p className="text-lg font-semibold text-gray-900">
              Sending cart for review
            </p>
            <p className="text-sm text-gray-500 text-center">
              Please wait — don&apos;t close this screen or start the sale yet.
            </p>
          </div>
        </div>
      )}

      {/* Checkout Info Modal */}
      <CheckoutInfoModal
        isOpen={checkoutOpen}
        onClose={handleCloseCheckout}
        onConfirm={handleConfirmCheckout}
        netAmount={roundedNetAmount}
        cashReceived={cashReceived}
        onCashReceivedChange={setCashReceived}
        bankReceived={bankReceived}
        onBankReceivedChange={setBankReceived}
        title="Checkout Info"
        confirmButtonText={isSubmitting ? "Processing..." : "Confirm"}
        cancelButtonText="Cancel"
        showBankReceived={true}
        autoFocus={true}
        disabled={isSubmitting}
        checkoutType="sale"
      />

      {/* Duplicate Item Modal */}
      {duplicateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Item already added
              </h3>
              <p className="text-sm text-gray-700">
                {duplicateItem?.Name} is already in the cart.
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setDuplicateModalOpen(false)}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Max Stock Modal */}
      {maxStockModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Maximum Stock Quantity reached
              </h3>
              <p className="text-sm text-gray-700">
                Cannot add more of {maxStockItem?.Name}. Available stock:{" "}
                {maxStockItem?.Stock}
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setMaxStockModalOpen(false);
                    // Focus barcode input after closing modal
                    setTimeout(() => {
                      if (searchInputRef.current) {
                        searchInputRef.current.focus();
                        searchInputRef.current.select();
                      }
                    }, 100);
                  }}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                  title="OK (Enter)"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zero Price Error Modal */}
      {zeroPriceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Invalid Item Price
                </h3>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                The following items have a price of 0 and cannot be sold. Please
                update their prices before proceeding:
              </p>
              <div className="bg-red-50 rounded-lg p-3 mb-4 max-h-48 overflow-y-auto">
                <ul className="space-y-2">
                  {zeroPriceItems.map((item, index) => (
                    <li
                      key={item.ID}
                      className="text-sm text-red-800 flex items-start gap-2"
                    >
                      <span className="font-medium">{index + 1}.</span>
                      <div>
                        <span className="font-medium">{item.Name}</span>
                        <span className="text-red-600 ml-2">
                          (ID: {item.ID})
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setZeroPriceModalOpen(false);
                    setZeroPriceItems([]);
                    // Focus barcode input after closing modal
                    setTimeout(() => {
                      if (searchInputRef.current) {
                        searchInputRef.current.focus();
                        searchInputRef.current.select();
                      }
                    }, 100);
                  }}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                  title="OK (Enter)"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      {successDialogOpen && saleData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.ctrlKey) {
              e.preventDefault();
              handleCloseSuccessDialog();
            }
          }}
          tabIndex={-1}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Sale Successful!
                </h3>
                <p className="text-sm text-gray-600">
                  Your sale has been completed successfully.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sale No:</span>
                    <span className="font-medium">
                      {saleData.SaleNo || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-medium text-green-600">
                      {formatPrice(saleData.totals?.netAmount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Items Sold:</span>
                    <span className="font-medium">
                      {saleData.items?.length || 0}
                    </span>
                  </div>
                  {saleData.CustomerName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Customer:</span>
                      <span className="font-medium">
                        {saleData.CustomerName}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCloseSuccessDialog}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                  title="Close (Enter)"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSInterface;
